import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PanelLeft, Moon, Sun, Search } from 'lucide-react';
import Sidebar from './Sidebar';
import QuickRecordModal from '../components/QuickRecordModal';
import GlobalSearch from '../components/GlobalSearch';
import { useDarkMode } from '../hooks/useDarkMode';
import { useLastScreen } from '../hooks/useLastScreen';
import { RESUME_WINDOW_MS, isReload, wasResumed } from '../utils/lastScreen';

const SIDEBAR_PREF_KEY = 'br_sidebar_collapsed';
// 빠른 기록 창이 열려 있던 시각. 앱이 꺼졌다 다시 열리면 창도 다시 연다
const QUICK_RECORD_OPEN_KEY = 'br_quick_record_open';

const wasQuickRecordOpen = () => {
  if (!(wasResumed() || isReload())) return false;
  try {
    const t = Number(localStorage.getItem(QUICK_RECORD_OPEN_KEY));
    return t > 0 && Date.now() - t <= RESUME_WINDOW_MS;
  } catch {
    return false;
  }
};

const AppShell = () => {
  const [darkMode, setDarkMode] = useDarkMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickRecordOpen, setQuickRecordOpen] = useState(wasQuickRecordOpen);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_PREF_KEY) === '1'; }
    catch { return false; }
  });
  const location = useLocation();

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_PREF_KEY, collapsed ? '1' : '0'); }
    catch { /* 저장 실패는 무시 */ }
  }, [collapsed]);

  useEffect(() => {
    try {
      if (quickRecordOpen) localStorage.setItem(QUICK_RECORD_OPEN_KEY, String(Date.now()));
      else localStorage.removeItem(QUICK_RECORD_OPEN_KEY);
    } catch { /* 저장 실패는 무시 */ }
  }, [quickRecordOpen]);

  // 보던 화면 기억 — 휴대폰이 앱을 껐다 다시 열면 스크롤 위치까지 되돌린다
  useLastScreen();

  // 라우트 이동 시 모바일 드로어 닫기 + 스크롤 최상단.
  // 첫 렌더는 건너뛴다 — useLastScreen 의 스크롤 복원과 부딪히기 때문.
  const firstRenderRef = useRef(true);
  useEffect(() => {
    setDrawerOpen(false);
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // 드로어/모달이 열린 동안 배경 스크롤 잠금.
  // body overflow 의 소유자를 이곳 한 군데로 두어야 두 오버레이가 서로의 해제를 덮어쓰지 않는다.
  useEffect(() => {
    const locked = drawerOpen || quickRecordOpen || searchOpen;
    document.body.style.overflow = locked ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen, quickRecordOpen, searchOpen]);

  // ⌘K / Ctrl+K 로 통합 검색
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-ink-950 text-ink-900 dark:text-ink-100">
      {/* 데스크톱 사이드바 */}
      {!collapsed && (
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 overflow-hidden border-r border-ink-200 dark:border-ink-800 z-30">
          <Sidebar
            onCollapse={() => setCollapsed(true)}
            onOpenQuickRecord={() => setQuickRecordOpen(true)}
          />
        </aside>
      )}

      {/* 모바일 드로어 */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-ink-950/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 overflow-hidden border-r border-ink-200 dark:border-ink-800 shadow-xl">
            <Sidebar
              onNavigate={() => setDrawerOpen(false)}
              onOpenQuickRecord={() => { setDrawerOpen(false); setQuickRecordOpen(true); }}
            />
          </aside>
        </div>
      )}

      <div className={collapsed ? '' : 'lg:pl-64'}>
        {/* 상단 바 */}
        <header className="sticky top-0 z-20 h-14 flex items-center gap-2 px-4 bg-white/90 dark:bg-ink-950/90 backdrop-blur-sm border-b border-ink-200 dark:border-ink-800">
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden p-1.5 text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="메뉴 열기"
          >
            <PanelLeft size={18} strokeWidth={1.5} />
          </button>
          {collapsed && (
            <button
              onClick={() => setCollapsed(false)}
              className="hidden lg:block p-1.5 text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
              aria-label="사이드바 펼치기"
            >
              <PanelLeft size={18} strokeWidth={1.5} />
            </button>
          )}

          <div className="flex-1" />

          <button
            onClick={() => setSearchOpen(true)}
            className="p-1.5 text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="검색"
            title="검색 (Ctrl+K)"
          >
            <Search size={16} strokeWidth={1.5} />
          </button>

          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-1.5 text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label={darkMode ? '라이트 모드로 전환' : '다크 모드로 전환'}
          >
            {darkMode
              ? <Sun size={16} strokeWidth={1.5} />
              : <Moon size={16} strokeWidth={1.5} />}
          </button>
        </header>

        <main className="px-4 sm:px-8 lg:px-12 py-10 pb-24">
          <div className="mx-auto w-full min-w-0 max-w-5xl">
            {/* 화면마다 코드를 따로 불러오므로(routes/index.jsx), 오는 동안 틀은 그대로 두고 본문만 기다린다 */}
            <Suspense fallback={<p className="label py-20 text-center">LOADING</p>}>
              <Outlet context={{ onOpenQuickRecord: () => setQuickRecordOpen(true) }} />
            </Suspense>
          </div>
        </main>
      </div>

      <QuickRecordModal
        open={quickRecordOpen}
        onClose={() => setQuickRecordOpen(false)}
      />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default AppShell;
